import { Box, Flex, forwardRef, HStack, Input, Text, Tooltip } from "@chakra-ui/react";
import { formatEther, parseEther } from "ethers/lib/utils";
import { useEffect, useState } from "react";
import { getAmountConfiguration } from "../../config/tokens";
import { formatToDisplay } from "../../helpers/numbers";
import { ERROR_AMOUNT, ERROR_AMOUNT_GREATER_THAN_MAX, ERROR_AMOUNT_LESS_THAN_MIN, ERROR_AMOUNT_NAN } from "./DcaErrors";

const AmountSelector = forwardRef(
  (
    {
      chainId,
      sourceToken,
      targetToken,
      amount,
      setAmount,
      length,
      dcaConfigErrors,
      setDcaConfigErrors,
      tooltip,
      ...props
    },
    ref
  ) => {
    const [minAmount, setMinAmount] = useState();
    const [maxAmount, setMaxAmount] = useState();
    const [step, setStep] = useState();

    // min/max/step configuration hook
    useEffect(() => {
      if (chainId && sourceToken && length >= 0) {
        const amtConfig = getAmountConfiguration(chainId, sourceToken, length);
        setMinAmount(amtConfig.minAmount);
        setMaxAmount(amtConfig.maxAmount);
        setStep(amtConfig.step);
      }
    }, [chainId, sourceToken, length]);

    // min/max amount validation hook
    // TODO: BUG Maximum update depth exceeded. when amount changes too much
    useEffect(() => {
      // bypass this when the min/max amounts are not set yet
      if (minAmount && maxAmount) {
        setDcaConfigErrors((prevErrs) => {
          let errs = { ...prevErrs };

          if (isNaN(amount)) {
            errs[ERROR_AMOUNT] = ERROR_AMOUNT_NAN;
          } else {
            if (amount.lt(minAmount)) {
              errs[ERROR_AMOUNT] = ERROR_AMOUNT_LESS_THAN_MIN;
            } else if (amount.gt(maxAmount)) {
              errs[ERROR_AMOUNT] = ERROR_AMOUNT_GREATER_THAN_MAX;
            } else {
              if (ERROR_AMOUNT in errs) delete errs[ERROR_AMOUNT];
            }
          }

          return errs;
        });
      }
    }, [amount, minAmount, maxAmount, setDcaConfigErrors]);

    // value change event handler
    const handleValueChange = (evt) => {
      try {
        setAmount(parseEther(evt.target.value));
      } catch {}
    };

    return (
      <Box ref={ref} {...props}>
        {/* title */}
        <Flex align="center" justify="space-between">
          <Text fontSize="lg" fontWeight="bold">
            💰 How much to invest?
          </Text>

          <Tooltip hasArrow bg="red.500" color="white" label={tooltip} fontSize="lg" placement="auto" closeDelay={500}>
            <Text fontSize="xs">ℹ️</Text>
          </Tooltip>
        </Flex>

        {/* label for total/daily amount */}
        {length > 0 ? <Text>Specify the TOTAL amount to spend</Text> : <Text>Specify the DAILY amount to buy</Text>}

        {/* daily/total amount */}
        <HStack>
          <Input
            variant="flushed"
            type="number"
            step={step}
            onChange={handleValueChange}
            defaultValue={formatEther(amount)}
          />
        </HStack>

        {/* label for min/max amount */}
        {minAmount && maxAmount && (
          <Text fontSize="xs" mt="0.25em">
            (from&nbsp;{formatToDisplay(formatEther(minAmount))}&nbsp;to&nbsp;
            {formatToDisplay(formatEther(maxAmount))}&nbsp;{sourceToken})
          </Text>
        )}
      </Box>
    );
  }
);

export default AmountSelector;
